import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SportsDataContext = createContext(null);

export function SportsDataProvider({ children }) {
  const [allGames, setAllGames] = useState([]);
  const [changedScores, setChangedScores] = useState({}); // {gameId: timestamp}
  const [loading, setLoading] = useState(false);
  const [leagues, setLeagues] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const prevScoresRef = useRef({});
  const hasLoadedOnceRef = useRef(false);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);

  // Detect user location
  useEffect(() => {
    async function detectLocation() {
      const apis = [
        { url: "https://ipapi.co/json/", parse: (d) => ({ city: d.city, region: d.region, regionCode: d.region_code, country: d.country_name, lat: d.latitude, lon: d.longitude }) },
        { url: "https://get.geojs.io/v1/ip/geo.json", parse: (d) => ({ city: d.city, region: d.region, regionCode: "", country: d.country, lat: parseFloat(d.latitude), lon: parseFloat(d.longitude) }) },
        { url: "https://freeipapi.com/api/json/", parse: (d) => ({ city: d.cityName, region: d.regionName, regionCode: "", country: d.countryName, lat: d.latitude, lon: d.longitude }) },
      ];
      for (const api of apis) {
        try {
          const resp = await axios.get(api.url, { timeout: 4000 });
          if (resp.data && (resp.data.city || resp.data.cityName)) {
            const loc = api.parse(resp.data);
            if (loc.city || loc.region) { setUserLocation(loc); return; }
          }
        } catch {}
      }
    }
    detectLocation();
  }, []);

  useEffect(() => {
    axios.get(`${API}/leagues`).then((r) => setLeagues(r.data)).catch(() => {});
  }, []);

  // Track score changes
  const updateGamesWithTracking = useCallback((newGames) => {
    const now = Date.now();
    const changes = {};
    const prev = prevScoresRef.current;
    for (const g of newGames) {
      const key = g.id;
      const oldHome = prev[key]?.homeScore;
      const oldAway = prev[key]?.awayScore;
      const newHome = g.homeTeam?.score;
      const newAway = g.awayTeam?.score;
      if (oldHome !== undefined && (oldHome !== newHome || oldAway !== newAway)) {
        changes[key] = now;
      }
      prev[key] = { homeScore: newHome, awayScore: newAway };
    }
    prevScoresRef.current = prev;
    if (Object.keys(changes).length > 0) {
      setChangedScores((old) => ({ ...old, ...changes }));
      // Clear flash after 3 seconds
      setTimeout(() => {
        setChangedScores((old) => {
          const updated = { ...old };
          for (const k of Object.keys(changes)) delete updated[k];
          return updated;
        });
      }, 3000);
    }
    setAllGames(newGames);
    setLastUpdated(now);
  }, []);

  const fetchAllScores = useCallback(async () => {
    // Only show skeleton on initial load; silent refresh thereafter to avoid screen flash
    if (!hasLoadedOnceRef.current) setLoading(true);
    try {
      const resp = await axios.get(`${API}/scores`);
      updateGamesWithTracking(resp.data.games || []);
      hasLoadedOnceRef.current = true;
    } catch (e) {
      console.error("Failed to fetch scores:", e);
    } finally {
      setLoading(false);
    }
  }, [updateGamesWithTracking]);

  const fetchLeagueScores = useCallback(async (leagueKey) => {
    try {
      const resp = await axios.get(`${API}/scores/${leagueKey}`);
      return resp.data.games || [];
    } catch { return []; }
  }, []);

  // WebSocket
  useEffect(() => {
    function connectWS() {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
      const wsUrl = backendUrl.replace("https://", "wss://").replace("http://", "ws://");
      try {
        const ws = new WebSocket(`${wsUrl}/api/ws/scores`);
        wsRef.current = ws;
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === "scores_update" && data.games) {
              updateGamesWithTracking(data.games);
            }
          } catch {}
        };
        ws.onclose = () => { reconnectTimer.current = setTimeout(connectWS, 3000); };
        ws.onerror = () => { ws.close(); };
      } catch {}
    }
    connectWS();
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    };
  }, [updateGamesWithTracking]);

  useEffect(() => {
    fetchAllScores();
    const pollInterval = setInterval(fetchAllScores, 10000);
    return () => clearInterval(pollInterval);
  }, [fetchAllScores]);

  return (
    <SportsDataContext.Provider value={{
      allGames, loading, leagues, userLocation, changedScores, lastUpdated, fetchAllScores, fetchLeagueScores,
    }}>
      {children}
    </SportsDataContext.Provider>
  );
}

export function useSportsData() {
  const ctx = useContext(SportsDataContext);
  if (!ctx) throw new Error("useSportsData must be used within SportsDataProvider");
  return ctx;
}
